import { useNavigate } from 'react-router-dom';
import { copyableErrorToast } from '@/lib/copyableErrorToast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import TextAreaChat from '@/components/TextAreaChat';
import { AttachmentThumbnailStrip } from '@/components/textAreaChat/AttachmentThumbnailStrip';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Content, Conversation } from '@shared/types';
import { MessageItem } from '../types/misc.ts';
import { SelectedItemsContext } from '@/contexts/SelectedItemsContext';
import * as Sentry from '@sentry/react';
import { useSendContentMutation } from '@/services/messageService';
import { setCachedOutputFormat } from '@/lib/outputFormatCache';
import { useHomepage } from '@/contexts/homepage-context';
import { isImageTier } from '@/components/textAreaChat/useImageModelLock';
import { inferColor } from '@/lib/colorInference';
import { UploadWell } from '@/components/promptCard/cardParts';
import { VALID_IMAGE_FORMATS } from '@/components/textAreaChat/fileTypes';

export interface TextToCadSectionProps {
  /**
   * 'rail' (default) is the original sidebar layout, unchanged. 'card' is the
   * centred prompt card: the upload well is promoted to the body and the
   * textarea becomes the optional prompt beneath it. Generation, model lock and
   * the registered Generate action are identical in both — only the shape
   * differs, so there is one owner of the generate pipeline.
   */
  variant?: 'rail' | 'card';
  /** Card variant only: which body to show. */
  mode?: 'image' | 'text';
  /** Card variant only: forwards TextAreaChat's prompt-text setter to the host
   *  so the card's "Try" row can fill the prompt. */
  onRegisterSetText?: (setText: (text: string) => void) => void;
}

export function TextToCadSection({
  variant = 'rail',
  mode = 'text',
  onRegisterSetText,
}: TextToCadSectionProps = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [images, setImages] = useState<MessageItem[]>([]);
  const [mesh, setMesh] = useState<MessageItem | null>(null);
  // #8: when the user uploads an image AND types a prompt, generation defaults
  // to editing the image via a ~8-120s Gemini seed-gen pass before the 3D
  // model runs (so the prompt's intent is honored). This toggle lets a user who
  // just wants their upload used as-is skip that pass entirely — the backend
  // already takes a fast, no-seed-gen path whenever no text is submitted (the
  // same path an empty prompt already takes), so this only ever STRIPS text
  // before submit; it changes no backend behavior.
  const [useImageDirectly, setUseImageDirectly] = useState(false);

  const {
    model,
    setModel,
    outputFormat,
    setOutputFormat,
    type,
    setType,
    setImageUploaded,
    registerSidebarAction,
  } = useHomepage();

  // Publish image-attached state up to the homepage so the sidebar's model
  // dropdown can lock to the image tiers (Magneto 0.1 / 0.2 / 0.3 / Trellis.2 / Hunyuan / Tripo) on upload.
  useEffect(() => {
    setImageUploaded(images.length > 0);
  }, [images.length, setImageUploaded]);

  // Generation pipeline is chosen by the MODEL, not the UI mode: selecting an
  // image tier (Trellis 2 / Hunyuan) generates a mesh while the sidebar stays
  // in its parametric layout. Keep `type` (passed to TextAreaChat) untouched so
  // the feature tree never changes — only the conversation we create routes.
  const genType: 'parametric' | 'imagetocad' = isImageTier(model)
    ? 'imagetocad'
    : type;

  const handleRegisterSubmit = useCallback(
    (
      fn: () => void,
      enabled: boolean,
      loading: boolean,
      stopFn?: () => void,
    ) => {
      registerSidebarAction({ fn, enabled, loading, stopFn });
    },
    [registerSidebarAction],
  );

  // TextAreaChat owns all upload handling (validation, size limits, storage,
  // preview generation). The card's dashed well just calls back into it, so
  // there is exactly one attach path no matter which affordance was used.
  const attachRef = useRef<((files: FileList) => void) | null>(null);
  const handleRegisterAttach = useCallback((add: (files: FileList) => void) => {
    attachRef.current = add;
  }, []);

  const handleTypeChange = (newType: 'parametric' | 'imagetocad') => {
    setType(newType);
    setModel(
      newType === 'imagetocad' ? 'meshy7' : 'google/gemini-3.1-pro-preview',
    );
  };

  const newConversationId = useMemo(() => crypto.randomUUID(), []);

  const { mutate: sendMessage } = useSendContentMutation({
    conversation: {
      id: newConversationId,
      user_id: user?.id ?? '',
      type: genType,
      settings: { model },
      current_message_leaf_id: null,
    },
  });

  const { mutate: handleGenerate } = useMutation({
    mutationFn: async (rawContent: Content) => {
      // #8: strip text when "use image directly" is on and there are images,
      // so generation takes the fast no-seed-gen path (same path an empty
      // prompt already takes) instead of editing the image via seed-gen.
      const content: Content =
        useImageDirectly && (rawContent.images?.length ?? 0) > 0
          ? { ...rawContent, text: undefined }
          : rawContent;

      const getRealUserId = async (): Promise<string> => {
        const {
          data: { session: cur },
        } = await supabase.auth.getSession();
        const id = cur?.user?.id;
        if (id && id !== '__god_mode__') return id;
        const { data: anonData, error: anonError } =
          await supabase.auth.signInAnonymously();
        if (anonData?.session?.user?.id) return anonData.session.user.id;
        throw new Error(
          anonError?.message
            ? `Auth failed: ${anonError.message}`
            : 'Anonymous sign-in not available.',
        );
      };
      const realUserId = await getRealUserId();

      const fmt = content.outputFormat ?? 'mesh';
      const color = inferColor(content.text ?? '');
      const { data: conversation, error: conversationError } = await supabase
        .from('conversations')
        .insert([
          {
            id: newConversationId,
            user_id: realUserId,
            title: 'New Conversation',
            type: genType,
            settings: { model, outputFormat: fmt, color },
          },
        ])
        .select()
        .single();

      if (conversationError) throw conversationError;

      setCachedOutputFormat(newConversationId, fmt);
      sendMessage(content);
      return { conversationId: conversation.id, content };
    },
    onSuccess: (data) => {
      supabase.functions
        .invoke('title-generator', {
          body: { content: data.content, conversationId: data.conversationId },
        })
        .then(({ data: titleData, error }) => {
          if (
            !error &&
            titleData?.title &&
            titleData.title !== 'New Conversation'
          ) {
            supabase
              .from('conversations')
              .update({ title: titleData.title })
              .eq('id', data.conversationId)
              .then(() => {
                queryClient.invalidateQueries({ queryKey: ['conversations'] });
                // Prefix filter so the title reaches EditorView's user-scoped
                // ['conversation', id, user?.id] entry, not just the bare
                // ['conversation', id] phantom (#295 key mismatch).
                queryClient.setQueriesData(
                  { queryKey: ['conversation', data.conversationId] },
                  (old: Conversation | undefined) =>
                    old ? { ...old, title: titleData.title } : old,
                );
              });
          }
        })
        .catch(console.error);
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      navigate(`/editor/${data.conversationId}`);
    },
    onError: (error) => {
      Sentry.captureException(error);
      // Toast with a built-in Copy button so users can grab the full
      // diagnostic (message + stack + URL) when this fires before a
      // conversation exists — there's no chat bubble to attach an
      // ErrorLogBubble to at this point in the flow.
      copyableErrorToast(error, {
        fallbackMessage: 'Failed to process prompt',
      });
    },
  });

  const isCard = variant === 'card';

  return (
    <SelectedItemsContext.Provider value={{ images, setImages, mesh, setMesh }}>
      {/* Card + image mode: the upload well is the primary affordance and the
          textarea drops to an optional prompt below it. The well writes into
          the SAME `images` state TextAreaChat's own attach button uses, so
          generation is byte-identical to the rail flow. */}
      {isCard && mode === 'image' && images.length === 0 && (
        <UploadWell
          accept={VALID_IMAGE_FORMATS.join(', ')}
          hint="JPG, PNG, WEBP"
          onFiles={(files) => attachRef.current?.(files)}
        />
      )}
      <TextAreaChat
        onSubmit={handleGenerate}
        conversation={{ id: newConversationId, user_id: user?.id ?? '' }}
        onFocus={() => {}}
        placeholder={
          isCard
            ? mode === 'image'
              ? 'Add a prompt to steer the result (optional)'
              : 'Use Telekinesis to move matter with your mind.'
            : 'Text-to-CAD Prompt:'
        }
        simplified={true}
        type={type}
        model={model}
        setModel={setModel}
        showPromptGenerator={true}
        showFullLabels={true}
        onTypeChange={handleTypeChange}
        initialOutputFormat={outputFormat}
        onOutputFormatChange={setOutputFormat}
        onRegisterSubmit={handleRegisterSubmit}
        onRegisterAttach={handleRegisterAttach}
        onRegisterSetText={onRegisterSetText}
        // The card renders its own upload well; the built-in attach row would
        // be a second control for the same action.
        hideAttachRow={isCard && mode === 'image'}
      />
      {/* Thumbnails of uploaded images, shown directly under the prompt. */}
      <AttachmentThumbnailStrip
        images={images}
        onRemove={(id) =>
          setImages((prev) => prev.filter((item) => item.id !== id))
        }
      />
      {/* #8: only relevant once an image is attached to an image-tier model —
          this is the only combination where a typed prompt triggers the slow
          seed-gen edit pass instead of using the upload as-is. */}
      {images.length > 0 && isImageTier(model) && (
        <div className="mt-2 flex items-center gap-2 px-1">
          <Switch
            id="use-image-directly"
            checked={useImageDirectly}
            onCheckedChange={setUseImageDirectly}
          />
          <Label
            htmlFor="use-image-directly"
            className="cursor-pointer text-xs text-telekinesis-neutral-300"
          >
            Use image as-is (faster — skips AI refinement of your prompt)
          </Label>
        </div>
      )}
    </SelectedItemsContext.Provider>
  );
}
